import React, { useState, useRef, useEffect } from 'react';
import { CiPause1 } from 'react-icons/ci';
import { FiPlay, FiSkipBack, FiSkipForward, FiSettings, FiRepeat } from 'react-icons/fi';
import { MdFullscreen, MdFullscreenExit } from 'react-icons/md';
import { IoIosArrowBack } from 'react-icons/io';
import { FaFlag } from 'react-icons/fa';
import { buildAutoCues, cueAtTime, readPlayerPrefs, writePlayerPrefs } from '../utils/playerPrefs';
import '../styles/videoPlayer.css';

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
const QUALITY_LADDER = [1080, 720, 480, 360]

const Video = props => {
  const prefs = readPlayerPrefs()
  const [isPlay, setPlay] = useState(false);
  const video = useRef(null);
  const player = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoBar, setVideoBar] = useState(0);
  const [fullVideo, setFullVideo] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuView, setMenuView] = useState('root');
  const [speed, setSpeed] = useState(Number(prefs.speed) || 1);
  const [autoplayNext, setAutoplayNext] = useState(prefs.autoplay !== false);
  const [quality, setQuality] = useState(prefs.quality || 'auto');
  const [captions, setCaptions] = useState(prefs.captions || 'off');
  const [nativeHeight, setNativeHeight] = useState(0);
  const [cues, setCues] = useState([]);
  const [cueText, setCueText] = useState('');
  const [buffering, setBuffering] = useState(true);
  const [ended, setEnded] = useState(false);

  const formatTime = seconds => {
    if (!seconds || Number.isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const applyQuality = () => {
    const el = video.current;
    const wrap = player.current;
    if (!el || !wrap) return;
    if (quality === 'auto' || props.compact) {
      el.style.width = '100%';
      el.style.height = '100%';
      el.style.maxHeight = '';
      el.style.objectFit = props.compact ? 'cover' : 'cover';
      return;
    }
    const target = Number(quality);
    if (!target) return;
    const ratio = (el.videoWidth && el.videoHeight) ? el.videoWidth / el.videoHeight : 16 / 9;
    const maxH = Math.min(wrap.clientHeight || target, target);
    const maxW = Math.min(wrap.clientWidth || maxH * ratio, maxH * ratio);
    el.style.width = `${Math.round(maxW)}px`;
    el.style.height = `${Math.round(maxH)}px`;
    el.style.maxHeight = `${target}px`;
    el.style.objectFit = 'contain';
  };

  const updateTime = () => {
    if (!video.current) return;
    const current = video.current.currentTime || 0;
    const durationValue = video.current.duration || 0;
    setCurrentTime(current);
    setDuration(durationValue);
    setVideoBar(durationValue > 0 ? (current / durationValue) * 100 : 0);
    if (captions === 'auto') {
      const cue = cueAtTime(cues, current);
      setCueText(cue ? cue.text : '');
    } else {
      setCueText('');
    }
    if (props.onProgress) {
      props.onProgress({
        time: current,
        playing: !video.current.paused,
        duration: durationValue
      });
    }
  };

  useEffect(() => {
    const el = video.current;
    if (!el) return;

    const handlePlay = () => { setPlay(true); setBuffering(false); setEnded(false); };
    const handlePause = () => setPlay(false);
    const handleWaiting = () => { if (!ended) setBuffering(true); };
    const handleReady = () => setBuffering(false);
    const handleEnded = () => {
      setPlay(false);
      setBuffering(false);
      setEnded(true);
      setMenuOpen(false);
      setShowControls(false);
      if (!props.compact && autoplayNext && props.onEnded) props.onEnded();
    };
    const handleFullscreen = () => {
      setFullVideo(document.fullscreenElement === player.current);
    };
    const handleMeta = () => {
      setNativeHeight(el.videoHeight || 0);
      applyQuality();
      updateTime();
      if (el.readyState >= 3) setBuffering(false);
    };

    el.addEventListener('loadedmetadata', handleMeta);
    el.addEventListener('loadeddata', handleReady);
    el.addEventListener('canplay', handleReady);
    el.addEventListener('playing', handleReady);
    el.addEventListener('waiting', handleWaiting);
    el.addEventListener('stalled', handleWaiting);
    el.addEventListener('timeupdate', updateTime);
    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);
    el.addEventListener('ended', handleEnded);
    document.addEventListener('fullscreenchange', handleFullscreen);
    window.addEventListener('resize', applyQuality);
    updateTime();

    return () => {
      if (props.onProgress && el) {
        props.onProgress({
          time: el.currentTime || 0,
          playing: false,
          duration: el.duration || 0
        });
      }
      el.removeEventListener('loadedmetadata', handleMeta);
      el.removeEventListener('loadeddata', handleReady);
      el.removeEventListener('canplay', handleReady);
      el.removeEventListener('playing', handleReady);
      el.removeEventListener('waiting', handleWaiting);
      el.removeEventListener('stalled', handleWaiting);
      el.removeEventListener('timeupdate', updateTime);
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handlePause);
      el.removeEventListener('ended', handleEnded);
      document.removeEventListener('fullscreenchange', handleFullscreen);
      window.removeEventListener('resize', applyQuality);
    };
  }, [props.data, autoplayNext, captions, cues, quality]);

  useEffect(() => {
    const el = video.current;
    if (!el) return undefined;
    const start = Number(props.startAt) || 0;
    const apply = async () => {
      if (start > 0.2) {
        try { el.currentTime = start; } catch (_) {}
        updateTime();
      }
      if (props.autoPlay) {
        try {
          if (el.readyState < 2) el.load();
          await el.play();
        } catch (_) {}
      }
    };
    if (el.readyState >= 1) apply();
    else el.addEventListener('loadedmetadata', apply, { once: true });
    return undefined;
  }, [props.data, props.startAt, props.autoPlay]);

  useEffect(() => {
    if (video.current) video.current.playbackRate = speed;
  }, [speed, props.data]);

  useEffect(() => {
    applyQuality();
  }, [quality, fullVideo]);

  useEffect(() => {
    if (captions !== 'auto') {
      setCues([]);
      setCueText('');
      return undefined;
    }
    const el = video.current;
    const total = el && el.duration ? el.duration : duration;
    setCues(buildAutoCues(props.captionText, total));
    return undefined;
  }, [captions, props.captionText, duration, props.data]);

  useEffect(() => {
    setBuffering(true);
    setEnded(false);
  }, [props.data]);

  const savePrefs = next => {
    const prefsNext = writePlayerPrefs(next);
    if (next.speed != null) setSpeed(prefsNext.speed);
    if (next.autoplay != null) setAutoplayNext(prefsNext.autoplay);
    if (next.quality != null) setQuality(prefsNext.quality);
    if (next.captions != null) setCaptions(prefsNext.captions);
  };

  const replayVideo = async event => {
    event?.stopPropagation?.();
    const el = video.current;
    if (!el) return;
    setEnded(false);
    setBuffering(true);
    try { el.currentTime = 0; } catch (_) {}
    try {
      if (el.readyState < 2) el.load();
      await el.play();
      setPlay(true);
    } catch (error) {
      setBuffering(false);
    }
  };

  const toggleControls = event => {
    event?.stopPropagation?.();
    if (props.onOpen) {
      props.onOpen();
      return;
    }
    if (ended && !autoplayNext) {
      replayVideo(event);
      return;
    }
    setShowControls(prev => !prev);
    if (showControls) {
      setMenuOpen(false);
      setMenuView('root');
    }
  };

  const videoHandel = async event => {
    event?.stopPropagation?.();
    if (!video.current) return;
    if (ended) {
      await replayVideo(event);
      return;
    }
    if (video.current.paused) {
      try {
        if (video.current.readyState < 2) video.current.load();
        await video.current.play();
        setPlay(true);
      } catch (error) {
        console.error('Video play failed', error);
      }
    } else {
      video.current.pause();
      setPlay(false);
    }
  };

  const VideoSpeed = (status, event) => {
    event?.stopPropagation?.();
    if (!video.current) return;
    if (status === '+') {
      video.current.currentTime = Math.max(0, video.current.currentTime - 10);
    } else {
      video.current.currentTime = Math.min(video.current.duration || 0, video.current.currentTime + 10);
    }
  };

  const seekTo = percent => {
    if (!video.current || !video.current.duration) return;
    const next = Math.min(100, Math.max(0, percent));
    video.current.currentTime = (next / 100) * video.current.duration;
    setVideoBar(next);
  };

  const handleSeek = event => {
    event.stopPropagation();
    seekTo(Number(event.target.value));
  };

  const toggleFull = async event => {
    event?.stopPropagation?.();
    if (props.disableFullscreen) return;
    const root = player.current;
    if (!root) return;
    try {
      if (!document.fullscreenElement) {
        await root.requestFullscreen();
        setFullVideo(true);
      } else {
        await document.exitFullscreen();
        setFullVideo(false);
      }
    } catch (error) {
      setFullVideo(prev => !prev);
    }
  };

  const qualityChoices = () => {
    const native = nativeHeight || 2160;
    return ['auto', ...QUALITY_LADDER.filter(height => height <= native + 24)];
  };

  const qualityLabel = value => {
    if (value === 'auto') return nativeHeight ? `Auto (${nativeHeight}p)` : 'Auto';
    return `${value}p`;
  };

  const playerClass = [
    'VideoPlayer',
    fullVideo ? 'fullscreen' : 'smallscreenVideo',
    isPlay ? 'is-playing' : 'is-paused',
    showControls || menuOpen ? 'is-controls' : '',
    props.onBack ? 'has-back' : '',
    props.compact ? 'is-compact' : '',
    quality !== 'auto' && !props.compact ? 'is-quality' : '',
    ended && !autoplayNext && !props.compact ? 'is-ended' : ''
  ].filter(Boolean).join(' ');

  return (
    <div ref={player} className={playerClass}>
      <video
        ref={video}
        preload="metadata"
        playsInline
        muted={!!props.muted}
        poster={props.poster}
        src={props.data || undefined}
        onClick={toggleControls}
      />

      {cueText && !props.compact && !ended ? <div className="videoCue">{cueText}</div> : null}
      {(buffering || props.busy) && !ended && !props.compact ? <div className="videoBuffer" role="status" aria-label="Loading video" /> : null}
      {ended && !autoplayNext && !props.compact ? (
        <button type="button" className="videoReplay" onClick={replayVideo} aria-label="Repeat video">
          <FiRepeat />
        </button>
      ) : null}

      {props.onBack ? (
        <button type="button" className="videoBackButton" onClick={event => { event.stopPropagation(); props.onBack(); }} aria-label="Back">
          <IoIosArrowBack />
        </button>
      ) : null}

      {props.compact ? null : (
      <div className="videoPlayerHeader" onClick={event => event.stopPropagation()}>
        <p>{props.title}</p>
        <p>
          {props.share ? <span>{props.share}</span> : null}
          {props.onReport ? (
            <button type="button" className="videoReportButton" onClick={props.onReport} aria-label="Report">
              <FaFlag />
            </button>
          ) : props.report ? <span>{props.report}</span> : null}
          <button
            type="button"
            className={`videoGearButton${menuOpen ? ' isOn' : ''}`}
            onClick={event => {
              event.stopPropagation();
              setMenuOpen(open => !open);
              setMenuView('root');
              setShowControls(true);
            }}
            aria-label="Settings"
          >
            <FiSettings />
          </button>
        </p>
      </div>
      )}

      <div className="videoControls" onClick={toggleControls}>

        <div className="buttomsVideo">
          {props.compact ? null : (
          <button type="button" className="backButton" onClick={event => VideoSpeed('+', event)}>
            <i><FiSkipBack /></i>
          </button>
          )}
          <button type="button" className="playButton" onClick={videoHandel}>
            {!isPlay ? <i><FiPlay /></i> : <i><CiPause1 /></i>}
          </button>
          {props.compact ? null : (
          <button type="button" className="frontButton" onClick={event => VideoSpeed('-', event)}>
            <i><FiSkipForward /></i>
          </button>
          )}
        </div>

        <div className="underLineVideo" onClick={event => event.stopPropagation()}>
          <div className="underLineVideoPlay">
            <p className="videoTime">{formatTime(currentTime)}</p>
            <input
              className="timeLine"
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={Number.isFinite(videoBar) ? videoBar : 0}
              style={{ background: `linear-gradient(to right, #69b4ff ${videoBar}%, rgba(255,255,255,0.22) ${videoBar}%)` }}
              onChange={handleSeek}
              onInput={handleSeek}
              aria-label="Seek"
            />
            <p className="videoTime">{formatTime(duration)}</p>
            {!props.disableFullscreen && (
              <button type="button" className="videoFullButton" onClick={toggleFull}>
                {fullVideo ? <MdFullscreenExit /> : <MdFullscreen />}
              </button>
            )}
          </div>
        </div>
      </div>

      {menuOpen && !props.compact ? (
        <div className="videoSettings" onClick={event => event.stopPropagation()}>
          {menuView !== 'root' ? (
            <button type="button" className="videoSettingsBack" onClick={() => setMenuView('root')}>Back</button>
          ) : null}
          {menuView === 'root' ? (
            <>
              <button type="button" onClick={() => setMenuView('quality')}>
                <span>Resolution</span>
                <em>{qualityLabel(quality)}</em>
              </button>
              <button type="button" onClick={() => setMenuView('captions')}>
                <span>Subtitles</span>
                <em>{captions === 'auto' ? 'Auto' : 'Off'}</em>
              </button>
              <button type="button" onClick={() => setMenuView('speed')}>
                <span>Speed</span>
                <em>{speed === 1 ? 'Normal' : `${speed}x`}</em>
              </button>
              <button type="button" onClick={() => setMenuView('autoplay')}>
                <span>Autoplay</span>
                <em>{autoplayNext ? 'On' : 'Off'}</em>
              </button>
            </>
          ) : null}
          {menuView === 'quality' ? qualityChoices().map(value => (
            <button
              key={value}
              type="button"
              className={String(quality) === String(value) ? 'isActive' : ''}
              onClick={() => { savePrefs({ quality: value }); setMenuView('root'); }}
            >
              {qualityLabel(value)}
            </button>
          )) : null}
          {menuView === 'captions' ? (
            <>
              <button type="button" className={captions === 'off' ? 'isActive' : ''} onClick={() => { savePrefs({ captions: 'off' }); setMenuView('root'); }}>Off</button>
              <button type="button" className={captions === 'auto' ? 'isActive' : ''} onClick={() => { savePrefs({ captions: 'auto' }); setMenuView('root'); }}>Auto generated</button>
            </>
          ) : null}
          {menuView === 'speed' ? SPEEDS.map(value => (
            <button
              key={value}
              type="button"
              className={speed === value ? 'isActive' : ''}
              onClick={() => { savePrefs({ speed: value }); setMenuView('root'); }}
            >
              {value === 1 ? 'Normal' : `${value}x`}
            </button>
          )) : null}
          {menuView === 'autoplay' ? (
            <>
              <button type="button" className={autoplayNext ? 'isActive' : ''} onClick={() => { savePrefs({ autoplay: true }); setMenuView('root'); }}>On</button>
              <button type="button" className={!autoplayNext ? 'isActive' : ''} onClick={() => { savePrefs({ autoplay: false }); setMenuView('root'); }}>Off</button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default Video;
